// vg-binding: P7.D-150
//
// apps/vendor/src/features/orders/detail/TrackingInputModal.tsx
//
// Phase 7 Wave 6 Task 21 — tracking input modal shown when the vendor
// clicks "Mark Shipped". Tracking is mandatory (BE returns
// TRACKING_REQUIRED 422 otherwise) — so the confirm button stays
// disabled until the input has a non-empty value.

import { useEffect, useState } from 'react';

import { Button, Input } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: (tracking: string) => void | Promise<void>;
}

export function TrackingInputModal({ open, onCancel, onConfirm }: Props): JSX.Element | null {
  const { t } = useTranslation('orders');
  const [tracking, setTracking] = useState('');

  useEffect(() => {
    if (!open) setTracking('');
  }, [open]);

  if (!open) return null;
  const canConfirm = tracking.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="tracking-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">
          {t('trackingModal.title')}
        </h2>
        <p className="mt-2 text-sm text-pw-text-muted">
          {t('trackingModal.description')}
        </p>

        <Input
          type="text"
          data-testid="tracking-input"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          placeholder="VN-EXP-12345..."
          className="mt-3"
        />

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="tracking-modal-cancel"
            onClick={onCancel}
          >
            {t('trackingModal.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="tracking-modal-confirm"
            onClick={() => void onConfirm(tracking.trim())}
            disabled={!canConfirm}
          >
            {t('trackingModal.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TrackingInputModal;
